<template>
  <!--
    The six stages of an order, as a strip of icon tiles.

    This replaces the six green circles on a dashed line at the top of the
    order timeline. Each stage has its own icon and tint so the strip can be
    read at a glance; a done stage is a solid green tick with "Complete", the
    stage the order is at shows its icon in colour with "In process…", and the
    stages still to come are faded with "Waiting…". The stages, their labels
    and which status lands where are exactly the old stepper's — this is the
    appearance only.
  -->
  <ol class="ostage" :aria-label="`Order progress: ${summary}`" data-test="order-stage-strip">
    <li
      v-for="(stage, index) in stages"
      :key="stage.key"
      class="ostage-step"
      :class="[`ostage-step--${stage.state}`, `ostage-step--${stage.key}`]"
      :aria-current="stage.state === 'current' ? 'step' : undefined"
      :data-state="stage.state"
    >
      <!-- The connector to the previous stage: solid once that stage is behind the order. -->
      <span v-if="index > 0" class="ostage-line" :class="{ 'ostage-line--done': stages[index - 1].state === 'done' }" aria-hidden="true"></span>

      <span class="ostage-tile" aria-hidden="true">
        <CheckIcon v-if="stage.state === 'done'" class="ostage-icon" />
        <component :is="ICONS[stage.key]" v-else class="ostage-icon" />
      </span>

      <span class="ostage-label">{{ stage.label }}</span>
      <span class="ostage-detail">{{ stage.detail }}</span>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { orderStages, type StageKey } from '@/utils/orderStages';
import {
  CheckBadgeIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PaintBrushIcon,
  SwatchIcon,
  TruckIcon,
} from '@heroicons/vue/24/outline';
import { computed, type Component } from 'vue';

const props = withDefaults(
  defineProps<{
    /** The order item's status key, e.g. "submitted_for_customer_review". */
    status: string;
  }>(),
  { status: '' }
);

const ICONS: Record<StageKey, Component> = {
  created: ClipboardDocumentListIcon,
  artwork: PaintBrushIcon,
  sample: SwatchIcon,
  production: Cog6ToothIcon,
  shipped: TruckIcon,
  completed: CheckBadgeIcon,
};

const stages = computed(() => orderStages(props.status));

const summary = computed(() => {
  const current = stages.value.find((stage) => stage.state === 'current');
  return current ? `${current.label} in process` : 'completed';
});
</script>

<style lang="scss">
/*
 * Sizes are in px on purpose: the app's root font size is 12.8px, so rem-based
 * utilities render at 80% of what they say.
 */
.ostage {
  @apply flex w-full list-none m-0 p-0 overflow-x-auto;
  min-width: 0;
}
.ostage-step {
  @apply relative flex flex-col items-center text-center;
  flex: 1 1 0;
  min-width: 96px;
  padding: 4px 6px 0;
  /* Per-stage tint: the tile's fill and the icon's colour. */
  --tint: var(--success-soft);
  --ink: var(--success);
}
.ostage-step--created { --tint: #dcf1ec; --ink: var(--success); }
.ostage-step--artwork { --tint: #fbe1ea; --ink: #b8446b; }
.ostage-step--sample { --tint: #e4e2fa; --ink: #5b51c4; }
.ostage-step--production { --tint: #efddf7; --ink: #8a47b0; }
.ostage-step--shipped { --tint: #fde7d3; --ink: #c46a1f; }
.ostage-step--completed { --tint: #fdf0c8; --ink: #a8790f; }

/* The connector sits behind the tile, from the previous tile's centre to this one's.
   Its span reaches left into the previous step, whose tile is a later sibling only
   within ITS own <li>; across steps the paint order is DOM order, so without an
   explicit stacking order the next step's line landed on top of the previous tile.
   z-index here (and a higher one on the tile) puts every line behind every tile. */
.ostage-line {
  @apply absolute block;
  top: 24px;
  right: 50%;
  left: -50%;
  height: 2px;
  background: #e6e8ec;
  z-index: 0;
}
.ostage-line--done {
  background: var(--success);
}

.ostage-tile {
  @apply relative flex items-center justify-center rounded-[12px];
  width: 40px;
  height: 40px;
  background: var(--tint);
  color: var(--ink);
  transition: background-color 200ms, color 200ms, opacity 200ms;
  /* Above every connector line (.ostage-line), including the next step's, which
     overlaps this tile from the left. */
  z-index: 1;
}
.ostage-icon {
  width: 22px;
  height: 22px;
}

.ostage-label {
  @apply mt-[8px] text-[12px] font-semibold text-ink leading-[15px];
  text-wrap: balance;
}
.ostage-detail {
  @apply mt-[2px] text-[11px] text-ink-soft leading-[14px] whitespace-nowrap;
}

/* Done: a solid green tick — progress is a state, so it takes the success
   colour, not the interactive accent (AB#21540). */
.ostage-step--done .ostage-tile {
  background: var(--success);
  color: #fff;
}
.ostage-step--done .ostage-icon {
  stroke-width: 2.5;
}

/* Current: the icon in its own colour, the label in the success green. */
.ostage-step--current .ostage-label {
  color: var(--success);
}
.ostage-step--current .ostage-detail {
  color: var(--ink);
}

/* Waiting: faded, so the eye lands on the current stage. */
.ostage-step--waiting .ostage-tile {
  opacity: 0.45;
}
.ostage-step--waiting .ostage-label {
  color: var(--ink-mute);
  font-weight: 500;
}
.ostage-step--waiting .ostage-detail {
  color: var(--ink-mute);
}

@media (prefers-reduced-motion: reduce) {
  .ostage-tile {
    transition: none;
  }
}
</style>
